// client/src/components/SiteInstructionModal.js
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { siteInstructionAPI } from '../utils/api';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

function displayName(u) {
  const n = [u.firstName, u.lastName].filter(Boolean).join(' ').trim();
  return n || u.username;
}

// Reusable multi-recipient input: pick from `suggestions` (users with an email)
// or type/paste raw addresses. Chips are { email, userId|null, name|null }.
function EmailChipsInput({ chips, onChange, suggestions, placeholder, label, info }) {
  const [text, setText] = useState('');
  const [open, setOpen] = useState(false);
  const [error, setError] = useState('');
  const boxRef = useRef(null);

  const chosen = useMemo(() => new Set(chips.map(c => c.email)), [chips]);

  const matches = useMemo(() => {
    const q = text.trim().toLowerCase();
    return (suggestions || [])
      .filter(u => !chosen.has(u.email.toLowerCase()))
      .filter(u => !q || u.email.toLowerCase().includes(q) || displayName(u).toLowerCase().includes(q) || (u.username || '').toLowerCase().includes(q))
      .slice(0, 8);
  }, [suggestions, text, chosen]);

  useEffect(() => {
    const onDocClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const addChip = (chip) => {
    if (chosen.has(chip.email)) return;
    onChange([...chips, chip]);
  };

  const addUser = (u) => {
    addChip({ email: u.email.toLowerCase(), userId: u.id, name: displayName(u) });
    setText(''); setError(''); setOpen(false);
  };

  // Commit typed text: split on comma/semicolon/space, validate each.
  const commitText = () => {
    const tokens = text.split(/[,;\s]+/).map(t => t.trim()).filter(Boolean);
    if (tokens.length === 0) return;
    const bad = [];
    const next = [...chips];
    const have = new Set(chosen);
    for (const t of tokens) {
      const lower = t.toLowerCase();
      if (!EMAIL_RE.test(t)) { bad.push(t); continue; }
      if (have.has(lower)) continue;
      have.add(lower);
      // If the typed address matches a known user, attach their id/name.
      const u = (suggestions || []).find(s => s.email.toLowerCase() === lower);
      next.push(u ? { email: lower, userId: u.id, name: displayName(u) } : { email: lower, userId: null, name: null });
    }
    onChange(next);
    if (bad.length) { setText(bad.join(', ')); setError(`Not a valid email: ${bad.join(', ')}`); }
    else { setText(''); setError(''); }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ';') { e.preventDefault(); commitText(); }
    else if (e.key === 'Backspace' && !text && chips.length) { onChange(chips.slice(0, -1)); }
  };

  const removeChip = (email) => onChange(chips.filter(c => c.email !== email));

  return (
    <div className="mb-2" ref={boxRef}>
      <div className="flex items-center gap-1 text-sm text-gray-700 mb-1">
        <span>{label}</span>
        {info && (
          <span className="relative group inline-flex">
            <svg className="w-3.5 h-3.5 text-gray-400 cursor-help" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span className="pointer-events-none absolute left-0 top-5 z-20 hidden group-hover:block w-64 rounded-md bg-gray-800 text-white text-xs p-2 shadow-lg">
              {info}
            </span>
          </span>
        )}
      </div>
      <div
        className="w-full min-h-[40px] px-2 py-1.5 border border-gray-300 rounded-lg flex flex-wrap gap-1 items-center focus-within:ring-2 focus-within:ring-primary-500"
        onClick={() => { setOpen(true); }}
      >
        {chips.map(c => (
          <span key={c.email} className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs ${c.userId ? 'bg-primary-100 text-primary-800' : 'bg-gray-100 text-gray-700'}`}>
            {c.name ? `${c.name} · ${c.email}` : c.email}
            <button type="button" className="text-gray-500 hover:text-red-600" onClick={(e) => { e.stopPropagation(); removeChip(c.email); }}>×</button>
          </span>
        ))}
        <input
          className="flex-1 min-w-[140px] text-sm outline-none py-0.5"
          value={text}
          placeholder={chips.length ? '' : placeholder}
          onChange={e => { setText(e.target.value); setOpen(true); setError(''); }}
          onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
          onBlur={commitText}
        />
      </div>
      {open && matches.length > 0 && (
        <div className="relative">
          <ul className="absolute z-20 mt-1 w-full max-h-52 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg text-sm">
            {matches.map(u => (
              <li key={u.id}>
                <button type="button" className="w-full text-left px-3 py-2 hover:bg-gray-50 flex justify-between gap-2" onMouseDown={(e) => { e.preventDefault(); addUser(u); }}>
                  <span className="text-gray-800">{displayName(u)}</span>
                  <span className="text-gray-400 text-xs truncate">{u.email}{u.role === 'contractor' ? '' : ` · ${u.role}`}</span>
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
    </div>
  );
}

export default function SiteInstructionModal({ pack, existing, onClose, onIssued }) {
  const [name, setName] = useState(pack.contractor_name || '');
  const [nameDirty, setNameDirty] = useState(!!pack.contractor_name);
  const [toChips, setToChips] = useState(
    pack.contractor_email ? [{ email: String(pack.contractor_email).toLowerCase(), userId: pack.contractor_user_id || null, name: pack.contractor_name || null }] : []
  );
  const [ccChips, setCcChips] = useState([]);
  const [reference, setReference] = useState('');
  const [message, setMessage] = useState('');
  const [file, setFile] = useState(null);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    let alive = true;
    siteInstructionAPI.getRecipients()
      .then(res => { if (alive) setUsers(res.data || []); })
      .catch(() => { /* picker just stays free-text only */ });
    return () => { alive = false; };
  }, []);

  const contractors = useMemo(() => users.filter(u => u.role === 'contractor'), [users]);

  // Auto-fill the recipient name from picked users until the consultant edits it.
  useEffect(() => {
    if (nameDirty) return;
    const names = toChips.map(c => c.name).filter(Boolean);
    setName(names.join(', '));
  }, [toChips, nameDirty]);

  const submit = async () => {
    setError('');
    if (!file) return setError('Please attach the cover letter PDF.');
    if (file.type !== 'application/pdf') return setError('The cover letter must be a PDF.');
    if (!name.trim()) return setError('Recipient name is required.');
    if (toChips.length === 0) return setError('Add at least one recipient email.');

    const toEmails = toChips.map(c => c.email);
    const ccEmails = ccChips.map(c => c.email).filter(e => !toEmails.includes(e));
    const userIds = toChips.map(c => c.userId).filter(Boolean);

    const fd = new FormData();
    fd.append('file', file);
    fd.append('recipientName', name.trim());
    fd.append('recipientEmails', JSON.stringify(toEmails));
    if (ccEmails.length) fd.append('ccEmails', JSON.stringify(ccEmails));
    if (userIds.length) fd.append('recipientUserIds', JSON.stringify(userIds));
    if (reference.trim()) fd.append('reference', reference.trim());
    if (message.trim()) fd.append('message', message.trim());
    setBusy(true);
    try {
      const res = await siteInstructionAPI.issue(pack.id, fd);
      setDone(res.data);
      onIssued && onIssued(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to issue Site Instruction.');
    } finally { setBusy(false); }
  };

  const doneToCount = done?.recipient_emails?.length || 1;
  const doneCcCount = done?.cc_emails?.length || 0;

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-lg p-6 rounded-lg shadow-xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">
          {existing ? 'Re-issue' : 'Issue'} Site Instruction — Station {pack.station_no}
        </h3>
        {existing && (
          <p className="text-xs text-amber-600 mb-3">
            Re-issuing supersedes the previous instruction and deactivates its link.
          </p>
        )}
        {done ? (
          <div className="text-sm text-emerald-700">
            Site Instruction issued to {doneToCount} recipient{doneToCount === 1 ? '' : 's'}{doneCcCount ? ` (cc ${doneCcCount})` : ''} and emailed.
            <div className="mt-4 text-right">
              <button className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        ) : (
          <>
            <EmailChipsInput
              label="Recipient email"
              info="Add multiple recipients — pick contractors from the list or type email addresses (comma-separated)."
              chips={toChips}
              onChange={setToChips}
              suggestions={contractors}
              placeholder="Select contractors or type an email…"
            />
            <label className="block text-sm text-gray-700 mb-2">
              Recipient name
              <input
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                value={name}
                onChange={e => { setName(e.target.value); setNameDirty(true); }}
                placeholder="Contractor full name"
              />
            </label>
            <EmailChipsInput
              label="CC (optional)"
              info="Copy others — pick any registered user or type email addresses (comma-separated)."
              chips={ccChips}
              onChange={setCcChips}
              suggestions={users}
              placeholder="Select users or type an email…"
            />
            <div className="flex gap-2">
              <label className="block text-sm text-gray-700 mb-2 flex-1">
                Reference (optional)
                <input
                  className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={reference}
                  onChange={e => setReference(e.target.value)}
                  placeholder="TO6"
                />
              </label>
            </div>
            <label className="block text-sm text-gray-700 mb-2">
              Message (optional)
              <textarea
                className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                rows={2}
                value={message}
                onChange={e => setMessage(e.target.value)}
              />
            </label>
            <label className="block text-sm text-gray-700 mb-2">
              Cover letter (PDF)
              <input
                ref={fileInputRef}
                type="file"
                accept="application/pdf"
                className="mt-1 w-full text-sm text-gray-700"
                onChange={e => setFile(e.target.files[0] || null)}
              />
            </label>
            <div className="bg-slate-50 rounded p-3 text-xs text-slate-600 my-3">
              <div className="font-medium mb-1">What will be sent (in order):</div>
              <ol className="list-decimal ml-4 space-y-0.5">
                <li>Site Instruction cover letter (your upload)</li>
                <li>DRE approval letter</li>
                <li>Approved documents</li>
              </ol>
            </div>
            {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
            <div className="flex justify-end gap-2 mt-4">
              <button
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                onClick={onClose}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 text-sm font-medium bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                onClick={submit}
                disabled={busy}
              >
                {busy ? 'Merging & sending…' : 'Generate & Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
